import React, { useState, useEffect } from 'react';
import { ProductDetailModal } from './modern_modal';
import { useGlobalToast } from './modern_toast';

function ModernProductCard({ produto, onAddToCart, onViewDetails }) {
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const { addToast } = useGlobalToast();

  // Verificar se o produto está nos favoritos ao carregar
  useEffect(() => {
    const favorites = JSON.parse(localStorage.getItem('favoritos') || '[]');
    const produtoId = produto.props?.key || produto.props?.detalhes?.COD_PRODUTO;
    setIsFavorite(favorites.includes(produtoId));
  }, [produto]);

  const handleAddToCart = async () => {
    setIsLoading(true);
    try {
      await onAddToCart(produto);
      addToast(
        `${produto.props?.detalhes?.DESC_PRODUTO} adicionado ao carrinho!`,
        'success'
      );
    } catch (error) {
      console.error('Erro ao adicionar ao carrinho:', error);
      addToast('Erro ao adicionar produto ao carrinho', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleFavorite = () => {
    const produtoId = produto.props?.key || produto.props?.detalhes?.COD_PRODUTO;
    const produtoNome = produto.props?.detalhes?.DESC_PRODUTO || 'Produto';
    
    if (!produtoId) {
      addToast('Erro: ID do produto não encontrado', 'error');
      return;
    }

    const favorites = JSON.parse(localStorage.getItem('favoritos') || '[]');
    let newFavorites;
    let message;
    
    if (isFavorite) {
      // Remover dos favoritos
      newFavorites = favorites.filter(id => id !== produtoId);
      message = `${produtoNome} removido dos favoritos`;
      setIsFavorite(false);
    } else {
      // Adicionar aos favoritos
      newFavorites = [...favorites, produtoId];
      message = `${produtoNome} adicionado aos favoritos!`;
      setIsFavorite(true);
    }
    
    localStorage.setItem('favoritos', JSON.stringify(newFavorites));
    addToast(message, isFavorite ? 'info' : 'success');
  };

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(produto);
    } else {
      setShowModal(true);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const hasDiscount = produto.props?.preco < produto.props?.preco_orig;

  return (
    <>
      <div className="product-card fade-in">
        {/* Imagem do produto */}
        <div className="product-image">
          {!imageError ? (
            <img
              src={produto.props?.img || '/placeholder-image.jpg'}
              alt={produto.props?.detalhes?.DESC_PRODUTO || 'Produto'}
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center bg-light" style={{ height: '200px' }}>
              <i className="bi bi-image fs-1 text-muted"></i>
            </div>
          )}
          
          {/* Badge de desconto */}
          {hasDiscount && (
            <div className="position-absolute top-0 start-0 m-2">
              <span className="badge bg-danger">
                {Math.round(((produto.props.preco_orig - produto.props.preco) / produto.props.preco_orig) * 100)}% OFF
              </span>
            </div>
          )}

          {/* Badge de estoque baixo */}
          {produto.props?.estoque && produto.props.estoque < 5 && (
            <div className="position-absolute top-0 end-0 m-2">
              <span className="badge bg-warning text-dark">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Últimas unidades
              </span>
            </div>
          )}
        </div>

        {/* Conteúdo do card */}
        <div className="p-3">
          {/* Título e grupo */}
          <div className="mb-2">
            <h6 className="card-title mb-1 fw-bold text-truncate">
              {produto.props?.detalhes?.DESC_PRODUTO || 'Produto sem nome'}
            </h6>
            <small className="text-muted">
              {produto.props?.detalhes?.GRUPO_PRODUTO || 'Categoria não informada'}
            </small>
          </div>

          {/* Detalhes do produto */}
          <div className="mb-3">
            {produto.props?.detalhes?.DESC_COR_PRODUTO && (
              <small className="d-block text-muted">
                <i className="bi bi-palette me-1"></i>
                {produto.props.detalhes.DESC_COR_PRODUTO}
              </small>
            )}
            {produto.props?.detalhes?.TAMANHO && (
              <small className="d-block text-muted">
                <i className="bi bi-rulers me-1"></i>
                {produto.props.detalhes.TAMANHO}
              </small>
            )}
            {produto.props?.estoque && (
              <small className="d-block text-muted">
                <i className="bi bi-box me-1"></i>
                {produto.props.estoque} em estoque
              </small>
            )}
          </div>

          {/* Preços */}
          <div className="mb-3">
            <div className="price-display">
              {hasDiscount && (
                <span className="price-original">
                  {formatPrice(produto.props.preco_orig)}
                </span>
              )}
              <span className="fw-bold">
                {formatPrice(produto.props?.preco || 0)}
              </span>
            </div>
          </div>

          {/* Ações */}
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-modern flex-fill"
              onClick={handleViewDetails}
              aria-label={`Ver detalhes de ${produto.props?.detalhes?.DESC_PRODUTO}`}
            >
              <i className="bi bi-eye"></i>
              <span className="btn-text ms-1">Detalhes</span>
            </button>

            <button
              className={`btn btn-modern flex-fill text-white ${isLoading ? 'disabled' : ''}`}
              onClick={handleAddToCart}
              disabled={isLoading || !produto.props?.estoque}
              aria-label={`Adicionar ${produto.props?.detalhes?.DESC_PRODUTO} ao carrinho`}
            >
              {isLoading ? (
                <>
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <span className="btn-text ms-1">Adicionando...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-cart-plus"></i>
                  <span className="btn-text ms-1">{produto.props?.estoque ? 'Adicionar' : 'Sem estoque'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Botão de favorito */}
        {/* <button
          className={`btn btn-link btn-favorite ${isFavorite ? 'is-favorite' : ''}`}
          aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          onClick={handleToggleFavorite}
        >
          <i className={`bi ${isFavorite ? 'bi-heart-fill' : 'bi-heart'} ${isFavorite ? 'text-danger' : 'text-muted'}`}></i>
        </button> */}
      </div>

      {/* Modal de detalhes */}
      <ProductDetailModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        produto={produto}
        onAddToCart={handleAddToCart}
      />
    </>
  );
}

export default ModernProductCard;
